import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function Stats() {
  const [growth, setGrowth] = useState([]);
  const [top, setTop] = useState([]);
  const [overview, setOverview] = useState(null);
  const [utm, setUtm] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/stats/growth"), api.get("/stats/top-posts"), api.get("/stats/overview"), api.get("/stats/utm-ranking")])
      .then(([g, t, o, u]) => { setGrowth(g.data); setTop(t.data); setOverview(o.data); setUtm(u.data); });
  }, []);

  const fmt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
  const engagement = overview ? ((overview.total_views / Math.max(1, overview.total_subscribers)) * 100).toFixed(1) : 0;

  return (
    <div data-testid="stats-page">
      <PageHeader title="Statistiche" subtitle="Analisi dettagliata di crescita, visualizzazioni ed engagement." />
      <PageContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-md p-5"><div className="text-xs uppercase font-mono text-slate-400">Engagement medio</div><div className="text-3xl font-extrabold text-blue-400 mt-2">{engagement}%</div></div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-5"><div className="text-xs uppercase font-mono text-slate-400">Visualizzazioni</div><div className="text-3xl font-extrabold text-white mt-2">{fmt(overview?.total_views)}</div></div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-5"><div className="text-xs uppercase font-mono text-slate-400">Post totali</div><div className="text-3xl font-extrabold text-white mt-2">{fmt((overview?.published_count||0)+(overview?.draft_count||0))}</div></div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-5"><div className="text-xs uppercase font-mono text-slate-400">Canali</div><div className="text-3xl font-extrabold text-white mt-2">{fmt(overview?.channels_count)}</div></div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
          <h3 className="font-bold text-white mb-1">Crescita iscritti + visualizzazioni</h3>
          <p className="text-xs text-slate-500 font-mono mb-4">ULTIMI 14 GIORNI · COMBINATO</p>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={growth}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis yAxisId="left" stroke="#3B82F6" fontSize={11} />
              <YAxis yAxisId="right" orientation="right" stroke="#38BDF8" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 6 }} />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="subscribers" stroke="#3B82F6" strokeWidth={2} name="Iscritti" />
              <Line yAxisId="right" type="monotone" dataKey="views" stroke="#38BDF8" strokeWidth={2} name="Views" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-md p-5" data-testid="utm-ranking">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-white">Ranking canali per click UTM</h3>
              <p className="text-xs text-slate-500 font-mono">CONVERSIONE & CTR</p>
            </div>
            <span className="text-[10px] font-mono px-2 py-1 rounded bg-blue-600/15 text-blue-300 border border-blue-500/30">UTM TRACKING</span>
          </div>
          {utm.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">Nessun canale.</div> : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 font-mono border-b border-slate-800">
                <tr><th className="text-left py-2">#</th><th className="text-left">Canale</th><th className="text-right">Iscritti</th><th className="text-right">Pubblicati</th><th className="text-right">Click</th><th className="text-right">CTR%</th></tr>
              </thead>
              <tbody>
                {utm.map((c, i) => (
                  <tr key={c.id} className="border-b border-slate-800/50">
                    <td className="py-2 font-mono text-slate-500">#{i+1}</td>
                    <td className="text-white font-medium">{c.name}</td>
                    <td className="text-right text-slate-400 font-mono">{c.subscribers}</td>
                    <td className="text-right text-slate-400 font-mono">{c.published}</td>
                    <td className="text-right text-sky-400 font-mono font-bold">{c.clicks}</td>
                    <td className="text-right text-emerald-400 font-mono font-bold">{c.ctr}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
          <h3 className="font-bold text-white mb-4">Top post per visualizzazioni</h3>
          {top.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">Nessun dato.</div> : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={top.map(p => ({ name: p.title.slice(0, 20), views: p.views }))}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 6 }} />
                <Bar dataKey="views" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </PageContent>
    </div>
  );
}
